import React, { useMemo, useState } from "react"

import { AlertCircle, ArrowLeft } from "lucide-react"
import { useNavigate } from "react-router-dom"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { type ApplicationDetails, useApplicationDetails } from "@/hooks/useApplicationDetails"
import { supabase } from "@/lib/supabase"

import DashboardLayout from "../layouts/DashboardLayout"

// Only mark difference if one has data and the other does not
const hasData = (val: unknown): boolean => {
  if (val === null || val === undefined) return false
  if (typeof val === "string") return val.trim().length > 0
  if (Array.isArray(val)) return val.length > 0
  if (typeof val === "object") return Object.keys(val as Record<string, unknown>).length > 0
  // numbers (including 0) and booleans count as data
  return true
}

const formatValue = (val: unknown): React.ReactNode => {
  if (val === null || val === undefined) return <span className="text-muted-foreground">—</span>
  if (Array.isArray(val)) return `${val.length} item${val.length === 1 ? "" : "s"}`
  if (typeof val === "boolean") return val ? "Yes" : "No"
  if (typeof val === "object") return "{…}" // avoid huge prints
  return String(val)
}

const FIELDS: Array<keyof ApplicationDetails | string> = [
  // Application
  "id",
  "application_id",
  "current_stage",
  "current_segment",
  "stage_reason",
  "stage_note",
  "status",
  "created_at",
  "updated_at",
  "contract_salary",
  "user_id",
  // Computed
  "name",
  "address_display",
  "headshot",
  "bank_proof_status",
  "allowed_stages",
  // Nested objects
  "applicant",
  "person",
  "primary_person",
  "job",
  "user",
  // Vehicle booking / Stripe
  "vehicle_booking",
  "stripe_customer_id",
  "stripe_payment_method_id",
  "payment_added_date",
  // Collections
  "documents",
  "orientation_bookings",
  "vehicle_bookings",
  "trial_blocks",
  "assessments",
  "driver_licence_records",
]

const ApplicationDiffDebug: React.FC = () => {
  const navigate = useNavigate()

  const [idAInput, setIdAInput] = useState<string>("") // application_id A
  const [idBInput, setIdBInput] = useState<string>("") // application_id B
  const [idAFetch, setIdAFetch] = useState<string>("")
  const [idBFetch, setIdBFetch] = useState<string>("")
  const [resolving, setResolving] = useState<boolean>(false)
  const [resolveError, setResolveError] = useState<string | null>(null)

  const {
    applicationDetails: appA,
    isLoading: loadingA,
    isError: isErrorA,
    error: errorA,
  } = useApplicationDetails(idAFetch || undefined)

  const {
    applicationDetails: appB,
    isLoading: loadingB,
    isError: isErrorB,
    error: errorB,
  } = useApplicationDetails(idBFetch || undefined)

  const isLoading = loadingA || loadingB
  const anyError = isErrorA || isErrorB || !!resolveError
  const errorMsg = resolveError || (errorA as Error)?.message || (errorB as Error)?.message

  const rows = useMemo(() => {
    const a = appA == null ? undefined : (appA as unknown as Record<string, unknown>)
    const b = appB == null ? undefined : (appB as unknown as Record<string, unknown>)

    const baseKeys = FIELDS.map((k) => String(k))
    const allKeysSet = new Set<string>(baseKeys)
    if (a) Object.keys(a).forEach((k) => allKeysSet.add(k))
    if (b) Object.keys(b).forEach((k) => allKeysSet.add(k))

    // Preserve order: base keys first, then extras alphabetically
    const extras = Array.from(allKeysSet)
      .filter((k) => !baseKeys.includes(k))
      .sort((x, y) => x.localeCompare(y))
    const orderedKeys = [...baseKeys, ...extras]

    return orderedKeys.map((key) => {
      const vA = a ? a[key] : undefined
      const vB = b ? b[key] : undefined
      const presentA = hasData(vA)
      const presentB = hasData(vB)
      const highlight = presentA !== presentB // Only presence/absence
      return { key, vA, vB, highlight }
    })
  }, [appA, appB])

  const resolveByApplicationId = async (applicationIdValue: string): Promise<string | null> => {
    const { data, error } = await supabase
      .from("applications")
      .select("id")
      .eq("application_id", applicationIdValue)
      .single()
    if (error) return null
    return (data as { id: string } | null)?.id ?? null
  }

  const handleFetch = async () => {
    const aAppId = idAInput.trim()
    const bAppId = idBInput.trim()
    if (!aAppId || !bAppId) {
      setResolveError("Please enter both application_id values.")
      return
    }

    setResolving(true)
    setResolveError(null)
    try {
      const [resolvedA, resolvedB] = await Promise.all([
        resolveByApplicationId(aAppId),
        resolveByApplicationId(bAppId),
      ])

      if (!resolvedA && !resolvedB) {
        setResolveError(
          `No applications found for application_id values: A='${aAppId}', B='${bAppId}'.`,
        )
        return
      }
      if (!resolvedA) {
        setResolveError(`Application A not found for application_id '${aAppId}'.`)
        return
      }
      if (!resolvedB) {
        setResolveError(`Application B not found for application_id '${bAppId}'.`)
        return
      }

      setIdAFetch(resolvedA)
      setIdBFetch(resolvedB)
    } finally {
      setResolving(false)
    }
  }

  return (
    <DashboardLayout>
      <div className="flex items-center mb-6">
        <Button variant="ghost" onClick={() => navigate("/applications")} className="mr-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Applications
        </Button>
        <h1 className="text-2xl font-bold">Application Diff Debug</h1>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Enter Two Application IDs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="col-span-1">
              <label className="text-sm text-muted-foreground">Application A application_id</label>
              <Input
                placeholder="Enter application A application_id"
                value={idAInput}
                onChange={(e) => setIdAInput(e.target.value)}
              />
            </div>
            <div className="col-span-1">
              <label className="text-sm text-muted-foreground">Application B application_id</label>
              <Input
                placeholder="Enter application B application_id"
                value={idBInput}
                onChange={(e) => setIdBInput(e.target.value)}
              />
            </div>
            <div className="col-span-1">
              <Button
                onClick={handleFetch}
                disabled={isLoading || resolving}
                className="w-full md:w-auto"
              >
                {isLoading || resolving ? "Loading..." : "Fetch"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {anyError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{errorMsg || "Failed to fetch applications"}</AlertDescription>
        </Alert>
      )}

      {(loadingA || loadingB) && (
        <div className="space-y-2 mb-6">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )}

      {(appA || appB) && (
        <Card>
          <CardHeader>
            <CardTitle>Side-by-Side Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Field</TableHead>
                    <TableHead>Application A</TableHead>
                    <TableHead>Application B</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.key} className={row.highlight ? "bg-yellow-50" : ""}>
                      <TableCell className="font-medium">{row.key}</TableCell>
                      <TableCell>{formatValue(row.vA)}</TableCell>
                      <TableCell>{formatValue(row.vB)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {!appA && !appB && !isLoading && (
        <p className="text-center py-4 text-muted-foreground">
          Enter two application IDs and click "Fetch" to compare.
        </p>
      )}
    </DashboardLayout>
  )
}

export default ApplicationDiffDebug
